import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Agreement,
  CheckEmail,
  CreatePassword,
  Profile,
  VerifyEmail,
} from '~/components/SignUp';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { useModal } from '~/hooks';
import { SignUpStep } from '~/types';
import {
  PAGE_ROUTE,
  signUpSteps,
  signUpStepsSNS,
  stepTitles,
} from '~/constants';
import AuthService from '~/services/AuthService';
import { useAuthStore, useSignUpStepStore } from '~/store';
import useToast from '~/store/useToast';

export default function SignUp() {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();

  const { step, setStep, setProgress, resetAll, type } = useSignUpStepStore(
    (state) => ({
      step: state.step,
      setStep: state.setStep,
      setProgress: state.setProgress,
      resetAll: state.resetAll,
      type: state.type,
    }),
  );

  const isOAuth = type === 'sns';
  const isLoading = useAuthStore((state) => state.isLoading);

  const totalProgress = useSignUpStepStore(
    (state) => state.progress + state.subProgress,
  );

  const title = stepTitles[step];
  const stepsList = isOAuth ? signUpStepsSNS : signUpSteps;

  const onNextStep = (curStep: SignUpStep) => () => {
    if (isOAuth) {
      switch (curStep) {
        case 'Agreement':
          setStep('Profile');
          break;
        case 'Profile':
          navigate(PAGE_ROUTE.SIGNUP_SUCCESS);
          break;
      }
    } else {
      switch (curStep) {
        case 'CheckEmail':
          setStep('CreatePassword');
          break;
        case 'CreatePassword':
          setStep('Agreement');
          break;
        case 'Agreement':
          setStep('Profile');
          break;
        case 'Profile':
          setStep('VerifyEmail');
          break;
        case 'VerifyEmail':
          navigate(PAGE_ROUTE.SIGNUP_SUCCESS);
          break;
      }
    }
  };

  const handleSignOut = async () => {
    const result = await AuthService.signOut();
    if (!result) return showToast('error', 'error sign out');
    resetAll();
    navigate(-1);
  };

  const handlePrev = () => {
    if (isOAuth) {
      switch (step) {
        case 'Agreement':
          openModal({
            title: 'Leave page',
            content:
              'If you leave, the current content and login will be deleted. Do you want to leave this page?',
            type: 'double_red',
            buttonName: 'Leave',
            click: handleSignOut,
          });
          break;
        default:
          setStep('Agreement');
          break;
      }
    } else {
      switch (step) {
        case 'CheckEmail':
          navigate(-1);
          resetAll();
          break;
        case 'CreatePassword':
          setStep('CheckEmail');
          break;
        case 'Profile':
          setStep('CreatePassword');
          break;
        case 'VerifyEmail':
          navigate(PAGE_ROUTE.SIGNIN);
          resetAll();
          showToast(
            'error',
            'Email verification is not complete. If you attempt to log in again, you will be redirected to the email verification page.',
          );
          break;
      }
    }
  };

  const renderStep = (stepName: SignUpStep) => {
    switch (stepName) {
      case 'CheckEmail':
        return <CheckEmail onNextStep={onNextStep('CheckEmail')} />;
      case 'CreatePassword':
        return <CreatePassword onNextStep={onNextStep('CreatePassword')} />;
      case 'Agreement':
        return <Agreement onNextStep={onNextStep('Agreement')} />;
      case 'Profile':
        return <Profile onNextStep={onNextStep('Profile')} />;
      case 'VerifyEmail':
        return <VerifyEmail onNextStep={onNextStep('VerifyEmail')} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    resetAll();
  }, []);

  useEffect(() => {
    const index = stepsList.indexOf(step);
    if (index === -1) {
      setProgress(0);
    } else {
      setProgress(index);
    }
  }, [step, stepsList]);

  return (
    <>
      <HelmetHead title='Sign up' />

      {isLoading && <FullScreenLoading />}

      <Layout
        steps={stepsList.length}
        currentStep={totalProgress}
        title={title}
        onPrevClick={handlePrev}
      >
        {renderStep(step)}
      </Layout>
    </>
  );
}
